import { apiRequest } from "./api";

export type LeaveType = "VACATION" | "SICK" | "EMERGENCY" | "MATERNITY" | "PATERNITY";
export type LeaveStatus = "PENDING" | "APPROVED" | "REJECTED";

export interface Leave {
  leaveId: string;
  userId: string;
  businessId?: string;
  employeeName?: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
  days: number;
  reason: string;
  status: LeaveStatus;
  reviewedBy?: string;
  reviewNote?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApplyLeavePayload {
  businessId: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
  reason: string;
  employeeName?: string;
}

export interface ReviewLeavePayload {
  businessId: string;
  userId: string;
  note?: string;
}

export interface ListLeavesResponse {
  items: Leave[];
  nextKey: string | null;
}

// ── Leave Balance / Credits ───────────────────────────────────────────────────

export interface LeavePolicy {
  creditsPerYear: number;
  updatedAt?: string;
  updatedBy?: string;
}

export interface LeaveBalanceAdjustment {
  delta: number;
  reason: string;
  adjustedBy: string;
  adjustedAt: string;
}

export interface LeaveBalance {
  userId: string;
  businessId: string;
  year: number;
  totalCredits: number;
  usedCredits: number;
  employeeName: string;
  adjustments: LeaveBalanceAdjustment[];
  updatedAt: string;
  updatedBy?: string;
}

export const leaveBalanceApi = {
  getPolicy: (businessId: string) =>
    apiRequest<{ policy: LeavePolicy | null }>(
      `/biz/${encodeURIComponent(businessId)}/leave/policy`,
    ),
  savePolicy: (businessId: string, creditsPerYear: number) =>
    apiRequest<{ policy: LeavePolicy }>(
      `/biz/${encodeURIComponent(businessId)}/leave/policy`,
      { method: "PUT", body: { creditsPerYear } },
    ),
  listBalances: (businessId: string, year: number) =>
    apiRequest<{ balances: LeaveBalance[] }>(
      `/biz/${encodeURIComponent(businessId)}/leave/balances`,
      { params: { year: String(year) } },
    ),
  setBalance: (
    businessId: string,
    userId: string,
    payload: { year: number; totalCredits: number; usedCredits: number; employeeName: string },
  ) =>
    apiRequest<{ balance: LeaveBalance }>(
      `/biz/${encodeURIComponent(businessId)}/leave/balances/${userId}`,
      { method: "PUT", body: payload },
    ),
  adjustBalance: (
    businessId: string,
    userId: string,
    payload: { delta: number; reason: string; year?: number },
  ) =>
    apiRequest<{ success: boolean }>(
      `/biz/${encodeURIComponent(businessId)}/leave/balances/${userId}/adjust`,
      { method: "POST", body: payload },
    ),
  bulkInit: (businessId: string, year: number) =>
    apiRequest<{ initialized: number; skipped: number }>(
      `/biz/${encodeURIComponent(businessId)}/leave/balances/init`,
      { method: "POST", body: { year } },
    ),
  getMyBalance: (businessId: string) =>
    apiRequest<{ balance: LeaveBalance | null }>(
      `/biz/${encodeURIComponent(businessId)}/my-leave-balance`,
    ),
};

export const leaveApi = {
  apply: (payload: ApplyLeavePayload) =>
    apiRequest<Leave>("/me/leaves", { method: "POST", body: payload }),
  listMine: (status?: LeaveStatus, params?: { limit?: number; cursor?: string }) =>
    apiRequest<{ items: Leave[]; nextCursor: string | null }>("/me/leaves", {
      params: {
        ...(status ? { status } : {}),
        limit: params?.limit ?? 10,
        ...(params?.cursor ? { cursor: params.cursor } : {}),
      },
    }),
  getMine: (leaveId: string) =>
    apiRequest<Leave>(`/me/leaves/${leaveId}`),
  adminList: (businessId: string, status = "PENDING", limit = 50, lastKey?: string) =>
    apiRequest<ListLeavesResponse>("/admin/leaves", { params: { businessId, status, limit, lastKey } }),
  approve: (leaveId: string, payload: ReviewLeavePayload) =>
    apiRequest<{ success: boolean }>(`/admin/leaves/${leaveId}/approve`, {
      method: "POST",
      body: payload,
    }),
  reject: (leaveId: string, payload: ReviewLeavePayload) =>
    apiRequest<{ success: boolean }>(`/admin/leaves/${leaveId}/reject`, {
      method: "POST",
      body: payload,
    }),
};
